import get from 'lodash/get';

import { WEBEDI_CELL_HEIGHT } from 'support/helpers/const/const';
import { EmbeddedTableRowRowSpan } from 'components/DataTable/Types';

const calculateGreatestCommonDivisor = (a: number, b: number): number => {
  return b === 0 ? a : calculateGreatestCommonDivisor(b, a % b);
};

const calculateLeastCommonMultiple = (a: number, b: number): number => {
  return (a * b) / calculateGreatestCommonDivisor(a, b);
};

/**
 * Calculates the LCM (Least Common Multiple) of all array lengths.
 *
 * LCM ensures that we create enough rows for each array item to span an integer
 * number of rows, enabling proper HTML rowSpan without fractional values.
 *
 * Example: arrays with [2, 3] items
 * - LCM(2, 3) = 6 rows total
 * - Array with 2 items: each item spans 6/2 = 3 rows
 * - Array with 3 items: each item spans 6/3 = 2 rows
 * - All rowSpan values are integers ✓
 */
export const calculateLeastCommonMultipleOfArrayLengths = (itemsCounts: Array<number>): number => {
  const nonZeroCounts = itemsCounts.filter((count) => count > 0);

  if (nonZeroCounts.length === 0) {
    return 1;
  }

  return nonZeroCounts.reduce((acc, num) => calculateLeastCommonMultiple(acc, num), 1);
};

/**
 * Calculates compressed row height to eliminate wasted vertical space.
 *
 * LCM creates more rows than visually needed (to ensure integer rowSpan).
 * Compression reduces each row's height proportionally so the total vertical
 * space equals what's actually needed to display the maximum number of items.
 *
 * Example: arrays with [2, 3] items
 * - LCM = 6 rows created (for math correctness)
 * - Max items = 3 (actual visual space needed)
 * - Compression factor = 6/3 = 2
 * - Row height = 40px / 2 = 20px per row
 * - Total space = 6 rows × 20px = 120px (same as 3 standard 40px rows) ✓
 */
export const calculateCompressedRowHeight = (totalRowsCount: number, maxItemsCount: number): number => {
  const compressionFactor = totalRowsCount / maxItemsCount;
  return WEBEDI_CELL_HEIGHT / compressionFactor;
};

/**
 * Extracts the length of each array field in the row data.
 * When withAddRows is true and the array has items, we count one extra item for the add button.
 * When withAddRows is true and the array is empty, we show only the add button (length = 1).
 */
export const getArrayLengthsPerParentPath = (
  rowData: unknown,
  paths: Array<string>,
  withAddRows: boolean,
): Record<string, number> => {
  return paths.reduce(
    (acc, path) => {
      const rawValue = get(rowData, path);
      const arrayLength = Array.isArray(rawValue) ? rawValue.length : 0;
      const addRowOffset = withAddRows ? 1 : 0;
      acc[path] = arrayLength + addRowOffset;
      return acc;
    },
    {} as Record<string, number>,
  );
};

export const calculateDataRowPixelHeight = (
  rowData: unknown,
  parentColumnPaths: Array<string>,
  enableEmbeddedRowActions: boolean,
): number => {
  if (parentColumnPaths.length === 0) {
    return WEBEDI_CELL_HEIGHT;
  }
  const arrayLengths = getArrayLengthsPerParentPath(rowData, parentColumnPaths, enableEmbeddedRowActions);
  const maxItems = Math.max(1, ...Object.values(arrayLengths));
  return maxItems * WEBEDI_CELL_HEIGHT;
};

/**
 * Determines which item in an array field should be displayed at a specific embedded table row.
 *
 * With LCM, each item can span multiple rows (rowsPerItem = totalEmbeddedRowsCount / itemsCount).
 * This calculates the index of the item to show at the current row position.
 *
 * When addRowEnabled is true, the last item in the embedded table is an add row, and it spans all
 * the sub-columns of that row.
 *
 * Example: totalEmbeddedRowsCount=6, itemsCount=2 → rowsPerItem=3
 * - Rows 0,1,2: show item 0
 * - Rows 3,4,5: show item 1
 */
export const createFieldInfo = ({
  itemsCount,
  embeddedTableRowIndex,
  embeddedTableRowsCount,
  addRowEnabled,
  subColumnsCount,
}: {
  /** Number of items in this specific array (including add row if enabled) */
  itemsCount: number | undefined;
  /** Which embedded table row (0 to embeddedTableRowsCount-1) we're rendering */
  embeddedTableRowIndex: number;
  /** Total embedded table rows created (LCM of all array lengths) */
  embeddedTableRowsCount: number;
  /** Whether to mark the last item as an add row */
  addRowEnabled: boolean;
  /** Number of sub-columns in the column group (for colSpan calculation) */
  subColumnsCount: number;
}): EmbeddedTableRowRowSpan => {
  if (!itemsCount) {
    return { itemIndex: -1, totalItems: 0, isAddRow: false, colSpan: undefined };
  }

  const rowsPerItem = embeddedTableRowsCount / itemsCount;
  const itemIndex = Math.floor(embeddedTableRowIndex / rowsPerItem);
  const isAddRow = addRowEnabled && itemIndex === itemsCount - 1;

  return {
    itemIndex,
    totalItems: itemsCount,
    isAddRow,
    colSpan: subColumnsCount,
  };
};
